import { FormHelperText, Modal, Stack } from "@mui/material";
import { getTemplateById, renameTemplate } from "apis/coachup";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import * as Yup from "yup";

const validationSchema = Yup.object({
  name: Yup.string()
    .max(100, "Name cannot be more than 100 characters")
    .required("Name is required"),
});

function RenameTemplate({ open, setOpen, template, setTemplate, getTemplates }) {
  const [initialValues, setInitialValues] = useState({
    name: template?.name || "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [httpError, setHttpError] = useState({ error: false, message: "" });

  const [controller] = useMaterialUIController();

  const handleClose = () => {
    if (!submitLoading) {
      setInitialValues({
        name: "",
      });
      setTemplate();
      setHttpError({ error: false, message: "" });
      setOpen(false);
    }
  };

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "90%" : 700,
    maxHeight: window.innerHeight < 800 ? 600 : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    borderRadius: "0.5rem",
    p: 4,
    outline: "none",
  };

  const getTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await getTemplateById(template);
      if (response?.data?.success) {
        setInitialValues({ name: response.data.data.name });
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      getTemplate();
    }
  }, [open]);

  const handleSubmit = async (data) => {
    try {
      setSubmitLoading(true);
      const response = await renameTemplate(template, { name: data.name });
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response.data.message || "Name Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        handleClose();
        getTemplates();
      } else if (response?.response?.status !== 403) {
        setHttpError({
          error: true,
          message: response?.response?.data?.message || "Something Went Wrong",
        });
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setSubmitLoading(false);
    } catch (error) {
      setSubmitLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <Loader loading={isLoading} />
          <MDTypography variant="h4" mb={2}>
            Rename Template
          </MDTypography>

          <MDBox sx={{ position: "relative" }}>
            <Formik
              enableReinitialize
              validateOnChange
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, touched, errors, dirty }) => (
                <Form>
                  <Stack
                    spacing={2}
                    sx={{
                      "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    }}
                  >
                    <MDInput
                      name="name"
                      label="Name"
                      value={values.name}
                      onChange={(event) => {
                        setFieldValue("name", event.target.value || "");
                      }}
                      helperText={touched.name && errors.name}
                      error={touched.name && Boolean(errors.name)}
                      sx={{ width: "60%" }}
                    />

                    {httpError.error && (
                      <MDBox
                        sx={{
                          "& .MuiFormHelperText-root": {
                            color: "#d32f2f",
                            fontSize: "16px",
                            mt: 2,
                            pl: 1,
                          },
                        }}
                      >
                        {" "}
                        <FormHelperText sx={{ marginTop: "0px !important" }}>
                          {httpError.message}
                        </FormHelperText>
                      </MDBox>
                    )}

                    <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                      <MDButton
                        color="gray80"
                        size="small"
                        onClick={handleClose}
                        sx={{ mt: 1, mr: 1, position: "relative" }}
                      >
                        Cancel
                      </MDButton>
                      <MDButton
                        color="primaryRed"
                        size="small"
                        type="submit"
                        disabled={submitLoading || !dirty}
                        sx={{ mt: 1, position: "relative" }}
                      >
                        {submitLoading ? "Loading..." : "Save"}
                      </MDButton>
                    </MDBox>
                  </Stack>
                </Form>
              )}
            </Formik>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default RenameTemplate;
